/**
 * 호스트 서비스 식별자 / locale 결정.
 *
 * iframe 진입 시 호스트 앱(DOKOCREW 등)이 URL 쿼리 `?service=DOKOCREW&locale=ko` 으로 전달한다.
 * 이후 내부 navigate 로 페이지 이동하면 쿼리가 사라지므로 첫 진입 시 sessionStorage 에
 * 저장해 같은 탭/iframe 의 후속 페이지가 안전하게 읽도록 한다.
 */

const SS_SERVICE = 'wiz-common.serviceId'
const SS_LOCALE = 'wiz-common.locale'

// 호스트가 서비스를 알려주지 않을 때의 기본 서비스 ID — 빌드 환경값(prod 만 새 ID), 미설정이면 옛 ID
export const DEFAULT_SERVICE_ID: string = process.env.REACT_APP_DEFAULT_SERVICE_ID || 'NEST'

const getQueryParam = (key: string): string | null => {
    try {
        return new URLSearchParams(window.location.search).get(key)
    } catch {
        return null
    }
}

const safeSessionRead = (key: string): string | null => {
    try {
        return window.sessionStorage.getItem(key)
    } catch {
        return null
    }
}

const safeSessionWrite = (key: string, value: string): void => {
    try {
        window.sessionStorage.setItem(key, value)
    } catch {
        // private 모드 등 — 무시
    }
}

/**
 * 서비스 식별자.
 *
 * 우선순위:
 *  1) URL 쿼리 `?service=DOKOCREW|DOKOPANG` (있으면 sessionStorage 갱신)
 *  2) sessionStorage (이전 진입 시 저장)
 *  3) `window.__APP__.appName` (RN WebView 환경에서 네이티브 주입)
 *  4) 기본값 — 빌드 환경값 REACT_APP_DEFAULT_SERVICE_ID (prod=DOKOPANG), 미설정(dev·local)이면 옛 ID NEST
 */
export const getServiceId = (): string => {
    const fromQuery = getQueryParam('service')?.toUpperCase()
    if (fromQuery) {
        safeSessionWrite(SS_SERVICE, fromQuery)
        return fromQuery
    }
    const fromSession = safeSessionRead(SS_SERVICE)
    if (fromSession) return fromSession
    const fromApp = window.__APP__?.appName?.toUpperCase()
    if (fromApp) return fromApp
    return DEFAULT_SERVICE_ID
}

/**
 * 디바이스 locale.
 *
 * 우선순위:
 *  1) URL 쿼리 `?locale=ko|en|ja` (있으면 sessionStorage 갱신)
 *  2) sessionStorage
 *  3) `window.__DEVICE__.language`
 *  4) navigator.language
 *  5) 기본값 ko
 */
export const getLocale = (): string => {
    const fromQuery = getQueryParam('locale')
    if (fromQuery) {
        safeSessionWrite(SS_LOCALE, fromQuery)
        return fromQuery
    }
    const fromSession = safeSessionRead(SS_LOCALE)
    if (fromSession) return fromSession
    if (window.__DEVICE__?.language) return window.__DEVICE__.language
    if (typeof navigator !== 'undefined' && navigator.language) {
        return navigator.language.split('-')[0]
    }
    return 'ko'
}
